import { useState, useEffect, useCallback, useMemo } from "react";
import { MondayColumn } from "shared/integrations/mapper";
import { PossibleTypesForFields, type OrgChartColumnMapping } from "./synced-org-chart.types";
import type { BoardWithColumns } from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import {
  ColumnForDirectManager,
  ColumnForName,
  ColumnForPosition,
  findAutoMappedColumnId,
  getFilteredAndSortedColumns,
} from "./utils";

export interface BoardWithColumnsAndCompatibility extends BoardWithColumns {
  isCompatible?: boolean;
}

export function useSyncedOrgChart(selectedBoard: BoardWithColumnsAndCompatibility | null) {
  const [columnMapping, setColumnMapping] = useState<OrgChartColumnMapping>({
    employeeName: null,
    directManager: null,
    position: null,
  });

  const [rootItem, setRootItem] = useState<{ id: string; name: string; displayName?: string } | null>(null);

  const columns: MondayColumn[] = useMemo(
    () => (selectedBoard?.columns ?? []) as MondayColumn[],
    [selectedBoard?.columns]
  );

  useEffect(() => {
    if (columns.length === 0) {
      setColumnMapping({ employeeName: null, directManager: null, position: null });
      setRootItem(null);
      return;
    }

    const autoMapping = {
      employeeName: findAutoMappedColumnId(columns, PossibleTypesForFields["Employee name"], ColumnForName),
      directManager: findAutoMappedColumnId(columns, PossibleTypesForFields["Direct manager"], ColumnForDirectManager),
      position: findAutoMappedColumnId(columns, PossibleTypesForFields.Position, ColumnForPosition),
    };

    setColumnMapping(
      autoMapping.employeeName || autoMapping.directManager || autoMapping.position
        ? autoMapping
        : {
            employeeName: null,
            directManager: null,
            position: null,
          }
    );

    setRootItem(null);
  }, [selectedBoard?.id, columns]);

  const handleColumnSelect = useCallback((field: keyof OrgChartColumnMapping, columnId: string) => {
    setColumnMapping((previous) => ({ ...previous, [field]: columnId }));
    if (field === "employeeName") {
      setRootItem(null);
    }
  }, []);

  const getColumnsForField = useCallback(
    (validTypes: readonly string[], selectedId: string | null) => {
      return getFilteredAndSortedColumns(columns, validTypes, selectedId);
    },
    [columns]
  );

  const isColumnEnabled = useCallback(
    (columnId: string, validTypes: readonly string[]) => {
      const column = columns.find((col) => col.id === columnId);
      return column ? validTypes.includes(column.type) : false;
    },
    [columns]
  );

  const allRequiredColumnsMapped = columnMapping.employeeName && columnMapping.directManager && columnMapping.position;

  const isCreateDisabled = !selectedBoard || !allRequiredColumnsMapped || !rootItem;

  return {
    columns,
    columnMapping,
    handleColumnSelect,
    allRequiredColumnsMapped,
    rootItem,
    setRootItem,
    isCreateDisabled,
    getColumnsForField,
    isColumnEnabled,
  };
}
